import { useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import AdminLayout from "../layout/AdminLayoutSewa";
import {
  adminCreateAgent,
  adminDeleteAgent,
  adminGetAgents,
  adminUpdateAgent,
} from "../api";

export default function UserManagementForm() {
  const [loading, setLoading] = useState(false);
  const [agents, setAgents] = useState([]);
  const [selectedAgentId, setSelectedAgentId] = useState("");

  const [createForm, setCreateForm] = useState({
    name: "",
    login_id: "",
    password: "",
    agent_type: "SEWA_PERUMAHAN",
    status: "ACTIVE",
  });

  const [editForm, setEditForm] = useState({
    name: "",
    login_id: "",
    password: "",
    agent_type: "",
    status: "",
  });

  const loadAgents = async () => {
    try {
      setLoading(true);
      const res = await adminGetAgents();
      setAgents(Array.isArray(res) ? res : []);
    } catch (e) {
      console.error(e);
      await Swal.fire({
        icon: "error",
        title: "Gagal",
        text: e?.response?.data?.error || "Gagal load senarai agent.",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAgents();
  }, []);

  useEffect(() => {
    const found = agents.find((x) => String(x.id) === String(selectedAgentId));

    if (!found) {
      setEditForm({
        name: "",
        login_id: "",
        password: "",
        agent_type: "",
        status: "",
      });
      return;
    }

    setEditForm({
      name: found.name || "",
      login_id: found.login_id || "",
      password: "",
      agent_type: found.agent_type || "",
      status: found.status || "ACTIVE",
    });
  }, [selectedAgentId, agents]);

  const selectedAgent = useMemo(
    () => agents.find((x) => String(x.id) === String(selectedAgentId)) || null,
    [agents, selectedAgentId]
  );

  const handleCreate = async () => {
    try {
      if (!createForm.name || !createForm.login_id || !createForm.password || !createForm.agent_type) {
        await Swal.fire({
          icon: "warning",
          title: "Maklumat tidak lengkap",
          text: "Sila isi semua medan wajib.",
        });
        return;
      }

      setLoading(true);
      await adminCreateAgent(createForm);

      await Swal.fire({
        icon: "success",
        title: "Berjaya",
        text: "Agent berjaya dicipta.",
      });

      setCreateForm({
        name: "",
        login_id: "",
        password: "",
        agent_type: "SEWA_PERUMAHAN",
        status: "ACTIVE",
      });

      await loadAgents();
    } catch (e) {
      console.error(e);
      await Swal.fire({
        icon: "error",
        title: "Gagal",
        text: e?.response?.data?.error || "Gagal cipta agent.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    try {
      if (!selectedAgentId) {
        await Swal.fire({
          icon: "warning",
          title: "Pilih agent",
          text: "Sila pilih agent untuk dikemaskini.",
        });
        return;
      }

      const payload = {
        name: editForm.name,
        login_id: editForm.login_id,
        agent_type: editForm.agent_type,
        status: editForm.status,
      };

      if (editForm.password && editForm.password.trim()) {
        payload.password = editForm.password.trim();
      }

      setLoading(true);
      await adminUpdateAgent(selectedAgentId, payload);

      await Swal.fire({
        icon: "success",
        title: "Berjaya",
        text: payload.password
          ? "Maklumat agent dan kata laluan berjaya dikemaskini."
          : "Maklumat agent berjaya dikemaskini.",
      });

      setEditForm((prev) => ({ ...prev, password: "" }));
      await loadAgents();
    } catch (e) {
      console.error(e);
      await Swal.fire({
        icon: "error",
        title: "Gagal",
        text: e?.response?.data?.error || "Gagal kemaskini agent.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (agent) => {
    const result = await Swal.fire({
      icon: "warning",
      title: "Padam agent?",
      text: `Anda pasti mahu padam ${agent.name}?`,
      showCancelButton: true,
      confirmButtonText: "Ya, padam",
      cancelButtonText: "Batal",
    });

    if (!result.isConfirmed) return;

    try {
      setLoading(true);
      await adminDeleteAgent(agent.id);

      await Swal.fire({
        icon: "success",
        title: "Berjaya",
        text: "Agent berjaya dipadam.",
      });

      if (String(selectedAgentId) === String(agent.id)) {
        setSelectedAgentId("");
      }

      await loadAgents();
    } catch (e) {
      console.error(e);
      await Swal.fire({
        icon: "error",
        title: "Gagal",
        text:
          e?.response?.data?.error ||
          "Gagal padam agent.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-5">
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-5">1. Pengguna Baharu (Agent)</h2>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
            <div>
              <label className="text-sm text-gray-700">Nama Penuh</label>
              <input
                className="w-full border rounded-md p-2 mt-1"
                value={createForm.name}
                onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                placeholder="Nama agent"
              />
            </div>

            <div>
              <label className="text-sm text-gray-700">Login ID</label>
              <input
                className="w-full border rounded-md p-2 mt-1"
                value={createForm.login_id}
                onChange={(e) => setCreateForm({ ...createForm, login_id: e.target.value })}
                placeholder="contoh: agent001"
              />
            </div>

            <div>
              <label className="text-sm text-gray-700">Kata Laluan</label>
              <input
                type="password"
                className="w-full border rounded-md p-2 mt-1"
                value={createForm.password}
                onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                placeholder="password"
              />
            </div>

            <div>
              <label className="text-sm text-gray-700">Jenis Akaun</label>
              <select
                className="w-full border rounded-md p-2 mt-1 bg-white"
                value={createForm.agent_type}
                onChange={(e) => setCreateForm({ ...createForm, agent_type: e.target.value })}
              >
                <option value="SEWA_PERUMAHAN">SEWA_PERUMAHAN</option>
                <option value="SEWA_PENJAJA">SEWA_PENJAJA</option>
              </select>
            </div>

            <button
              onClick={handleCreate}
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-700 text-white rounded-md px-4 py-2"
            >
              Cipta
            </button>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-5">2. Kemaskini Agent</h2>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="text-sm text-gray-700">Pilih Agent</label>
              <select
                className="w-full border rounded-md p-2 mt-1 bg-white"
                value={selectedAgentId}
                onChange={(e) => setSelectedAgentId(e.target.value)}
              >
                <option value="">-- Pilih Agent --</option>
                {agents.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name} ({a.login_id})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-sm text-gray-700">Nama Penuh</label>
              <input
                className="w-full border rounded-md p-2 mt-1"
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
              />
            </div>

            <div>
              <label className="text-sm text-gray-700">Login ID</label>
              <input
                className="w-full border rounded-md p-2 mt-1"
                value={editForm.login_id}
                onChange={(e) => setEditForm({ ...editForm, login_id: e.target.value })}
              />
            </div>

            <div>
              <label className="text-sm text-gray-700">Status</label>
              <select
                className="w-full border rounded-md p-2 mt-1 bg-white"
                value={editForm.status}
                onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
              >
                <option value="">Pilih Status</option>
                <option value="ACTIVE">ACTIVE</option>
                <option value="INACTIVE">INACTIVE</option>
              </select>
            </div>

            <div>
              <label className="text-sm text-gray-700">Jenis Akaun</label>
              <select
                className="w-full border rounded-md p-2 mt-1 bg-white"
                value={editForm.agent_type}
                onChange={(e) => setEditForm({ ...editForm, agent_type: e.target.value })}
              >
                <option value="">Pilih Jenis</option>
                <option value="SEWA_PERUMAHAN">SEWA_PERUMAHAN</option>
                <option value="SEWA_PENJAJA">SEWA_PENJAJA</option>
              </select>
            </div>

            <div>
              <label className="text-sm text-gray-700">Password Baru</label>
              <input
                type="password"
                className="w-full border rounded-md p-2 mt-1"
                value={editForm.password}
                onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                placeholder="Kosongkan jika tidak tukar"
              />
            </div>

            <div className="flex items-end">
              <button
                onClick={handleUpdate}
                disabled={loading || !selectedAgentId}
                className="bg-blue-600 hover:bg-blue-700 text-white rounded-md px-5 py-2"
              >
                Kemaskini
              </button>
            </div>

            <div className="flex items-end gap-2">
              {selectedAgent ? (
                <>
                  <a
                    href={`/admin/agent-units?agentId=${selectedAgent.id}&template=${selectedAgent.agent_type === "SEWA_PENJAJA" ? "penjaja" : "perumahan"}`}
                    className="inline-block bg-green-600 hover:bg-green-700 text-white rounded-md px-5 py-2"
                  >
                    Tugaskan Unit
                  </a>

                  <button
                    onClick={() => handleDelete(selectedAgent)}
                    className="bg-red-600 hover:bg-red-700 text-white rounded-md px-5 py-2"
                  >
                    Padam
                  </button>
                </>
              ) : null}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Senarai Agent</h2>

          <div className="overflow-x-auto">
            <table className="w-full text-sm border">
              <thead className="bg-gray-50">
                <tr>
                  <th className="p-2 border text-left">No</th>
                  <th className="p-2 border text-left">Nama</th>
                  <th className="p-2 border text-left">Login ID</th>
                  <th className="p-2 border text-left">Jenis</th>
                  <th className="p-2 border text-left">Status</th>
                  <th className="p-2 border text-left">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {agents.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="p-3 text-center text-gray-500">
                      Tiada data.
                    </td>
                  </tr>
                ) : (
                  agents.map((a, idx) => (
                    <tr key={a.id} className="border-t">
                      <td className="p-2 border">{idx + 1}</td>
                      <td className="p-2 border font-semibold">{a.name}</td>
                      <td className="p-2 border">{a.login_id}</td>
                      <td className="p-2 border">{a.agent_type}</td>
                      <td className="p-2 border">{a.status}</td>
                      <td className="p-2 border">
                        <div className="flex gap-2">
                          <button
                            onClick={() => setSelectedAgentId(String(a.id))}
                            className="px-3 py-1 rounded bg-blue-100 text-blue-700"
                          >
                            Edit
                          </button>

                          <a
                            href={`/admin/agent-units?agentId=${a.id}&template=${a.agent_type === "SEWA_PENJAJA" ? "penjaja" : "perumahan"}`}
                            className="px-3 py-1 rounded bg-green-100 text-green-700"
                          >
                            Assign Units
                          </a>

                          <button
                            onClick={() => handleDelete(a)}
                            className="px-3 py-1 rounded bg-red-100 text-red-700"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}